/**
 * LYR Theme — Global JavaScript
 * Handles: cart drawer, quick add, mobile nav toggle, search overlay
 * No dependencies — vanilla JS only.
 */

(function () {
  'use strict';

  // ─── Utilities ───────────────────────────────────────────────────────────────

  function on(el, event, fn, opts) {
    if (!el) return;
    el.addEventListener(event, fn, opts || false);
  }

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  function trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  // ─── Cart API helpers ─────────────────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item to cart');
    }
    return res.json();
  }

  async function updateCartItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
    return res.json();
  }

  // ─── Cart count update ────────────────────────────────────────────────────────

  async function refreshCartCount() {
    try {
      const cart = await fetchCart();
      const countEls = qsa('.site-header__cart-count');
      countEls.forEach(function (el) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
      // Update aria-label on cart button
      const cartBtn = qs('[data-action="open-cart"]');
      if (cartBtn) {
        cartBtn.setAttribute('aria-label', 'Open cart (' + cart.item_count + ' items)');
      }
    } catch (e) {
      console.warn('Cart count refresh failed:', e);
    }
  }

  // ─── Cart Drawer ──────────────────────────────────────────────────────────────

  const cartDrawer = {
    el: null,
    overlay: null,

    init: function () {
      this.el = qs('#CartDrawer');
      this.overlay = qs('#CartOverlay');
      if (!this.el) return;

      const closeBtns = qsa('[data-action="close-cart"]', this.el);
      closeBtns.forEach(function (btn) { on(btn, 'click', cartDrawer.close.bind(cartDrawer)); });
      if (this.overlay) on(this.overlay, 'click', this.close.bind(this));

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape') cartDrawer.close();
      });
    },

    open: async function () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      this.el.removeAttribute('hidden');
      if (this.overlay) { this.overlay.removeAttribute('hidden'); this.overlay.setAttribute('aria-hidden', 'false'); }
      document.body.style.overflow = 'hidden';
      try {
        await this.render();
      } catch (e) {
        console.warn('Cart render error:', e);
      }
      trapFocus(this.el);
      const firstFocusable = qs('a, button, input', this.el);
      if (firstFocusable) firstFocusable.focus();
    },

    close: function () {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'true');
      this.el.hidden = true;
      if (this.overlay) { this.overlay.hidden = true; this.overlay.setAttribute('aria-hidden', 'true'); }
      document.body.style.overflow = '';
      const cartBtn = qs('[data-action="open-cart"]');
      if (cartBtn) cartBtn.focus();
    },

    render: async function () {
      const cart = await fetchCart();
      const body = qs('.cart-drawer__body', this.el);
      const countEl = qs('.cart-drawer__count', this.el);
      const totalEl = qs('.cart-drawer__total-price', this.el);

      if (countEl) countEl.textContent = cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items');
      if (totalEl) totalEl.textContent = '$' + (cart.total_price / 100).toFixed(2);

      if (!body) return;

      if (cart.items.length === 0) {
        body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
        return;
      }

      body.innerHTML = cart.items.map(function (item) {
        return [
          '<div class="cart-item" data-key="' + item.key + '">',
            '<div class="cart-item__image">',
              item.image
                ? '<img src="' + item.image.replace('.jpg', '_120x.jpg') + '" alt="' + item.title + '" width="80" height="80" loading="lazy">'
                : '',
            '</div>',
            '<div class="cart-item__info">',
              '<p class="cart-item__title">' + item.product_title + '</p>',
              item.variant_title !== 'Default Title'
                ? '<p class="cart-item__variant">' + item.variant_title + '</p>'
                : '',
              '<div class="cart-item__controls">',
                '<button class="cart-item__qty-btn" data-action="decrement" data-key="' + item.key + '" aria-label="Decrease quantity" type="button">−</button>',
                '<span class="cart-item__qty">' + item.quantity + '</span>',
                '<button class="cart-item__qty-btn" data-action="increment" data-key="' + item.key + '" aria-label="Increase quantity" type="button">+</button>',
                '<button class="cart-item__remove" data-action="remove" data-key="' + item.key + '" aria-label="Remove ' + item.title + '" type="button">Remove</button>',
              '</div>',
            '</div>',
            '<p class="cart-item__price">$' + (item.final_line_price / 100).toFixed(2) + '</p>',
          '</div>'
        ].join('');
      }).join('');

      // Bind quantity controls
      qsa('[data-action="decrement"], [data-action="increment"], [data-action="remove"]', body).forEach(function (btn) {
        on(btn, 'click', async function () {
          const key = btn.dataset.key;
          const item = cart.items.find(function (i) { return i.key === key; });
          if (!item) return;
          let newQty = item.quantity;
          if (btn.dataset.action === 'increment') newQty += 1;
          if (btn.dataset.action === 'decrement') newQty -= 1;
          if (btn.dataset.action === 'remove')    newQty = 0;
          try {
            await updateCartItem(key, newQty);
            await cartDrawer.render();
            await refreshCartCount();
          } catch (e) {
            console.warn('Cart update failed:', e);
          }
        });
      });
    }
  };

  // ─── Quick Add ────────────────────────────────────────────────────────────────

  on(document, 'click', async function (e) {
    const btn = e.target.closest('[data-action="quick-add"]');
    if (!btn) return;

    const variantId = btn.dataset.productId;
    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      await addToCart(parseInt(variantId, 10), 1);
      await refreshCartCount();
      btn.textContent = 'Added!';
      setTimeout(function () {
        btn.textContent = 'Quick Add';
        btn.disabled = false;
      }, 1500);
      cartDrawer.open();
    } catch (err) {
      btn.textContent = 'Sold Out';
      setTimeout(function () {
        btn.textContent = 'Quick Add';
        btn.disabled = false;
      }, 2000);
    }
  });

  // Quick add with options — open quick-add drawer
  on(document, 'click', function (e) {
    const btn = e.target.closest('[data-action="quick-add-options"]');
    if (!btn) return;
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    // Navigate to product page on mobile, or open quick-add drawer if available
    const quickAdd = qs('#QuickAddDrawer');
    if (quickAdd) {
      quickAdd.dataset.handle = handle;
      // Implementation can be expanded in snippets/quick-add.liquid
    } else {
      window.location.href = '/products/' + handle;
    }
  });

  // ─── Cart open button ─────────────────────────────────────────────────────────

  on(document, 'click', function (e) {
    const btn = e.target.closest('[data-action="open-cart"]');
    if (!btn) return;
    cartDrawer.open();
  });

  // ─── Mobile Nav ───────────────────────────────────────────────────────────────

  const mobileNav = {
    toggle: null,
    nav: null,

    init: function () {
      this.toggle = qs('[data-action="toggle-mobile-nav"]');
      this.nav = qs('#MobileNav');
      if (!this.toggle || !this.nav) return;

      on(this.toggle, 'click', this.handleToggle.bind(this));

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && mobileNav.nav.classList.contains('is-open')) {
          mobileNav.close();
        }
      });
    },

    handleToggle: function () {
      if (this.nav.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open: function () {
      this.nav.classList.add('is-open');
      this.nav.removeAttribute('hidden');
      this.nav.setAttribute('aria-hidden', 'false');
      this.toggle.setAttribute('aria-expanded', 'true');
      // Swap icons
      const openIcon  = qs('.icon-open', this.toggle);
      const closeIcon = qs('.icon-close', this.toggle);
      if (openIcon)  openIcon.style.display  = 'none';
      if (closeIcon) closeIcon.style.display = 'block';
    },

    close: function () {
      this.nav.classList.remove('is-open');
      this.nav.setAttribute('aria-hidden', 'true');
      this.toggle.setAttribute('aria-expanded', 'false');
      const openIcon  = qs('.icon-open', this.toggle);
      const closeIcon = qs('.icon-close', this.toggle);
      if (openIcon)  openIcon.style.display  = '';
      if (closeIcon) closeIcon.style.display = 'none';
    }
  };

  // ─── Search overlay ───────────────────────────────────────────────────────────

  on(document, 'click', function (e) {
    const btn = e.target.closest('[data-action="open-search"]');
    if (!btn) return;
    // Redirect to search page — a full predictive search drawer
    // can be added later via snippets/search-drawer.liquid
    window.location.href = '/search';
  });

  // ─── Init ─────────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    cartDrawer.init();
    mobileNav.init();
    refreshCartCount();
  });

})();
